import { memo, useRef, useEffect } from 'react';
import { CustomCellEditorProps } from 'ag-grid-react';
import { Autocomplete, TextField, Box } from '@mui/material';

type Option = { label: string; value: string | number };

export const MultiSelectCellEditor = memo(
  ({ value: values, options = [], onValueChange }: CustomCellEditorProps & { options?: Option[] }) => {
    const refAutocomplete = useRef<HTMLDivElement>(null);

    useEffect(() => {
      refAutocomplete.current?.focus();
    }, []);

    const selectedOptions = Array.isArray(values)
      ? values
          .map((value) => options.find((option) => option.value === value))
          .filter((option): option is Option => !!option)
      : [];

    const handleChange = (_event: React.SyntheticEvent, newValue: Option[]) =>
      onValueChange(newValue.map((option) => option.value));

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Autocomplete
          ref={refAutocomplete}
          multiple
          value={selectedOptions}
          onChange={handleChange}
          options={options}
          getOptionLabel={({ label }) => label}
          isOptionEqualToValue={(option, { value: currentValue }) => option.value === currentValue}
          disableClearable
          disableCloseOnSelect
          size="small"
          sx={{ width: '100%', whiteSpace: 'nowrap', overflow: 'hidden' }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              size="small"
              placeholder="Select the template(s)"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: null,
                },
              }}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                '& :is(.MuiOutlinedInput-root, .MuiInputBase-root)': {
                  maxWidth: '100%',
                  overflow: 'hidden',
                },
                '& .MuiOutlinedInput-root': {
                  minHeight: 'auto',
                  padding: 0,
                  position: 'relative',
                  flexWrap: 'nowrap',
                  boxSizing: 'border-box',
                  paddingRight: '4rem',
                  '& fieldset': { border: 'none' },
                },
                '& .MuiAutocomplete-input': {
                  position: 'static',
                  padding: '0.25rem',
                  minWidth: '1.875rem',
                },
              }}
            />
          )}
        />
      </Box>
    );
  },
);

MultiSelectCellEditor.displayName = 'MultiSelectCellEditor';
